import { useEffect, useState } from "react";

import Box from "@/components/Box";
import { theme } from "@/core/theme";
import { ORGANISATION_BASKET_APPLICABLE_AUTO_APPLY_DISCOUNTS_QUERY } from "@/graphql/organisations/queries/organisation";
import useBreakPointBasedProp from "@/hooks/useBreakPointBasedProp";
import ChevronComponent from "@/tpo/Chevron";
import Menu from "@/tpo/Menu";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useLazyQuery } from "@apollo/client";

const COLUMNS = "repeat(4, minmax(0, 1fr)) auto";

const ROW_BORDER = { borderTop: "1px solid", borderColor: "lightGrey" };

function redemptionsRemaining({ available, used }) {
  if (available === null || available === undefined) {
    return "Unlimited";
  }
  return Number(available) - Number(used || 0);
}

function expiry({ validTo }) {
  if (!validTo) {
    return "No expiry";
  }
  return new Date(validTo).toLocaleDateString("en-GB", {
    year: "2-digit",
    month: "numeric",
    day: "numeric"
  });
}

function Cell({ children, ...props }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      px={4}
      py={4}
      fontSize={[14, 14, 16]}
      lineHeight={1.5}
      fontFamily="gilroyMedium"
      color="dark"
      overflowWrap="anywhere"
      {...props}
    >
      {children}
    </Box>
  );
}

function MobileFieldRow({ label, children, valueTestId, valueProps = {} }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
      <Box
        fontFamily="gilroyBold"
        fontSize={[14, 14, 16]}
        lineHeight={1.5}
        color="midGrey"
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {label}
      </Box>
      <Box
        flex={1}
        fontSize={[14, 14, 16]}
        lineHeight={1.5}
        fontFamily="gilroyMedium"
        color="dark"
        textAlign="right"
        overflowWrap="anywhere"
        data-testid={valueTestId}
        {...valueProps}
      >
        {children}
      </Box>
    </Box>
  );
}

function SuggestionList({ suggestions, disabled, onApply, onClose, stacked }) {
  if (stacked) {
    return (
      <Box px={theme.spacing.panelBox.px} pb={theme.spacing.small}>
        {(suggestions ?? []).map(({ discount }, index) => (
          <Box
            key={discount.id}
            data-testid="auto-apply-discount-suggestion"
            borderTop={index === 0 ? "none" : "1px solid"}
            borderColor="lightGrey"
            pt={theme.spacing.medium}
            pb={index < suggestions.length - 1 ? theme.spacing.medium : 0}
          >
            <Stack gap={10}>
              <MobileFieldRow
                label="Discount code"
                valueTestId="suggestion-code"
                valueProps={{ textDecoration: "underline" }}
              >
                {discount.code}
              </MobileFieldRow>
              <MobileFieldRow label="Discount value" valueTestId="suggestion-value">
                {discount.value}
              </MobileFieldRow>
              <MobileFieldRow label="Available" valueTestId="suggestion-remaining">
                {redemptionsRemaining(discount)}
              </MobileFieldRow>
              <MobileFieldRow label="Expires" valueTestId="suggestion-expiry">
                {expiry(discount)}
              </MobileFieldRow>
            </Stack>
            <Box display="flex" justifyContent="flex-end" pt={theme.spacing.medium}>
              <ButtonV2
                type="button"
                color="dark"
                size="xs"
                withChevron
                sx={{ borderRadius: 4, letterSpacing: "1.5px" }}
                disabled={disabled}
                onClick={() => {
                  onClose();
                  onApply(discount.code);
                }}
              >
                Apply
              </ButtonV2>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box display="grid" gridTemplateColumns={COLUMNS} minWidth={660}>
      {["Discount code", "Discount value", "Available", "Expires", ""].map(heading => (
        <Cell
          key={heading}
          fontFamily="gilroyBold"
          fontSize={14}
          lineHeight="24px"
          whiteSpace="nowrap"
        >
          {heading}
        </Cell>
      ))}
      {(suggestions ?? []).map(({ discount }) => (
        <Box display="contents" key={discount.id} data-testid="auto-apply-discount-suggestion">
          <Cell {...ROW_BORDER} textDecoration="underline" data-testid="suggestion-code">
            {discount.code}
          </Cell>
          <Cell {...ROW_BORDER} data-testid="suggestion-value">
            {discount.value}
          </Cell>
          <Cell {...ROW_BORDER} data-testid="suggestion-remaining">
            {redemptionsRemaining(discount)}
          </Cell>
          <Cell {...ROW_BORDER} fontFamily="gilroyMedium" data-testid="suggestion-expiry">
            {expiry(discount)}
          </Cell>
          <Cell {...ROW_BORDER} justifyContent="flex-end">
            <ButtonV2
              type="button"
              color="dark"
              size="xs"
              withChevron
              sx={{ borderRadius: 4, letterSpacing: "1.5px" }}
              disabled={disabled}
              onClick={() => {
                onClose();
                onApply(discount.code);
              }}
            >
              Apply
            </ButtonV2>
          </Cell>
        </Box>
      ))}
    </Box>
  );
}

export default function AutoApplyDiscountSuggestions({
  organisationId,
  /** Optional: stories and unit tests supply suggestions instead of querying. */
  suggestions: suggestionsOverride,
  onApply,
  disabled
}) {
  const [opened, setOpened] = useState(false);
  const [loadSuggestions, { data, loading: suggestionsLoading, called }] = useLazyQuery(
    ORGANISATION_BASKET_APPLICABLE_AUTO_APPLY_DISCOUNTS_QUERY,
    { fetchPolicy: "network-only" }
  );

  const useQuery = suggestionsOverride === undefined;
  const stackedLayout = useBreakPointBasedProp([true, true, false]);

  useEffect(() => {
    if (!useQuery || !opened || !organisationId) {
      return;
    }
    loadSuggestions({
      variables: { organisation: parseInt(organisationId, 10) }
    });
  }, [useQuery, opened, organisationId, loadSuggestions]);

  const suggestions =
    suggestionsOverride ?? data?.basket?.applicableAutoApplyDiscounts ?? undefined;

  if (useQuery) {
    if (!organisationId) {
      return null;
    }
    if (called && !suggestionsLoading && !suggestions?.length) {
      return null;
    }
  } else if (!suggestions?.length) {
    return null;
  }

  const listLoading = useQuery && opened && suggestionsLoading;

  return (
    <Menu opened={opened} onChange={setOpened}>
      <Menu.Target>
        <Box
          as="button"
          type="button"
          disabled={disabled}
          data-testid="auto-apply-discount-suggestions-button"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mt={theme.spacing.medium}
          px={theme.spacing.panelBox.px}
          py={3}
          bg="white"
          border="1px solid"
          borderColor="lightGrey"
          borderRadius={5}
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight={1.5}
          color="midGrey"
          textAlign="left"
          cursor={disabled ? "not-allowed" : "pointer"}
          opacity={disabled ? 0.5 : 1}
        >
          Available discounts
          <ChevronComponent
            direction={opened ? "top" : "bottom"}
            fill={theme.colors.dark}
            width={7}
          />
        </Box>
      </Menu.Target>
      <Menu.Dropdown
        mt={0}
        border="1px solid"
        borderColor="haze"
        borderRadius="0 0 12px 12px"
        boxShadow="0 4px 8px rgba(26, 26, 26, 0.08)"
        maxHeight={400}
        overflow="auto"
        data-testid="auto-apply-discount-suggestions"
      >
        {listLoading ? (
          <Cell px={theme.spacing.panelBox.px} py={5} color="midGrey">
            Loading…
          </Cell>
        ) : (
          <SuggestionList
            suggestions={suggestions}
            disabled={disabled}
            onApply={onApply}
            onClose={() => setOpened(false)}
            stacked={stackedLayout}
          />
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
